// components/charts/ProcessedTodayBarChart.jsx
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, Cell } from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../shadcn-components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../shadcn-components/ui/chart";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import getProcessedTodayUserCount from "../../api/getProcessedTodayUserCount";
import "../../styles/Dashboard.css";

const availableColors = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
];

export function ProcessedTodayBarChart({ token, customBoxShadow }) {
  const [chartData, setChartData] = useState([]);
  const [chartConfig, setChartConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate(); // ← router helper

  useEffect(() => {
    async function fetchData() {
      const res = await getProcessedTodayUserCount({ token });
      const users = res.data;

      const sortedUsers = users.sort(
        (a, b) => b.document_count - a.document_count
      );

      const top4Users = sortedUsers.slice(0, 4);
      const otherUsers = sortedUsers.slice(4);

      const otherDocumentCount = otherUsers.reduce(
        (sum, user) => sum + user.document_count,
        0
      );

      const newChartData = [
        ...top4Users.map((user, index) => ({
          assign_user: user.assign_user,
          assign_user_id: user.assign_user_id,
          document_count: user.document_count,
          fill: availableColors[index],
        })),
        ...(otherUsers.length > 0
          ? [
              {
                assign_user: "Other",
                document_count: otherDocumentCount,
                fill: availableColors[4],
              },
            ]
          : []),
      ];

      const newChartConfig = {
        ...top4Users.reduce((acc, user, index) => {
          acc[user.assign_user] = {
            label: user.assign_user,
            color: availableColors[index],
          };
          return acc;
        }, {}),
        Other: {
          label: "Other",
          color: availableColors[4],
        },
      };

      setChartData(newChartData);
      setChartConfig(newChartConfig);
      setLoading(false);
    }

    fetchData();
  }, [token]);

  // === click handler ======================================================
  const handleBarClick = ({ assign_user_id }) => {
    // avoid navigating on the aggregated “Other” bar
    if (assign_user_id) {
      navigate(
        `/processed-documents?assignUser=${encodeURIComponent(assign_user_id)}`
      );
    }
  };
  // ========================================================================

  return (
    <Card style={customBoxShadow}>
      <CardHeader>
        <CardTitle>Processed Today</CardTitle>
        <CardDescription>Users who processed documents today</CardDescription>
      </CardHeader>
      <CardContent>
        {!loading && chartData.length === 0 ? (
          <div className="flex items-center justify-center text-sm text-muted-foreground h-[250px]">
            No documents processed today
          </div>
        ) : (
          <ChartContainer config={chartConfig}>
            <BarChart data={chartData} margin={{ top: 24 }}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="assign_user"
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                tickFormatter={(value) =>
                  value?.length > 7 ? `${value.slice(0, 7)}…` : value
                }
              />
              <ChartTooltip cursor={false} content={<ChartTooltipContent />} />

              {/* Each Cell gets its own click handler */}
              <Bar dataKey="document_count" radius={8}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.fill}
                    cursor={entry.assign_user !== "other" ? "pointer" : "default"}
                    onClick={() => handleBarClick(entry)}
                  />
                ))}
                <LabelList
                  position="top"
                  offset={12}
                  className="fill-foreground"
                  fontSize={14}
                />
              </Bar>
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
